import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { BrandColors } from '../../global/helpers-marketing';
import type { UIBadgeContentType, UIBadgeLayout } from './ui-badge.type';
import styles from './ui-badge.css';
import {
    BadgeContentTypes,
    BadgeLayouts,
    validateBadgeColor,
    validateBadgeContentType,
    validateBadgeLayout,
} from './ui-badge.helpers';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIBadge
 * @element ui-badge
 * @classdesc Represents a class for <code>ui-badge</code> element.
 * Small badge in the corner for advertising purposes.
 * @property {UIBadgeLayout} [layout] {@attr layout} Shape of the badge.
 * @property {UIBadgeContentType} [contentType] {@attr content-type} A layout can have different types for different usages.
 * @property {UIBrandColor | "yellow"} [color] {@attr color} Color of the badge.
 * @slot
 * @example
 * <ui-badge>Badge</ui-badge>
 * <ui-badge layout="pin" color="orange">1</ui-badge>
 * <ui-badge color="orange" layout="pin"><ui-icon glyph="redesign-heart"></ui-icon></ui-badge>
 * <ui-badge color="yellow" layout="segment">Gold customer</ui-badge>
 * <ui-badge color="lilac" layout="segment-small">Gold customer</ui-badge>
 */
@Component({ tag: 'ui-badge', styles })
export class UIBadge extends UIElement {
    /**
     * @type {UIBadgeLayouts}
     */
    static get Layout() {
        return BadgeLayouts;
    }

    /**
     * @type {UIBadgeContentTypes}
     */
    static get ContentType() {
        return BadgeContentTypes;
    }
    /**
     * @param {UIBadge} component
     * @param {string} value
     * @private
     */
    static validateColor = (component: UIBadge, value: string) => {
        return validateBadgeColor(component, value);
    };

    /**
     * @param {UIBadge} component
     * @param {string} value
     * @private
     */
    static validateLayout = (component: UIBadge, value: string) => {
        return validateBadgeLayout(component, value);
    };

    /**
     * @param {UIBadge} component
     * @param {string} value
     * @private
     */
    static validateContentType = (component: UIBadge, value: string) => {
        return validateBadgeContentType(component, value);
    };

    @Attribute({ type: String, validate: validateBadgeColor })
    color: BrandColors = BrandColors.Turquoise;

    @Attribute({ type: String, validate: validateBadgeLayout })
    layout: UIBadgeLayout = UIBadge.Layout.Default;

    @Attribute({ type: String, validate: validateBadgeContentType })
    contentType: UIBadgeContentType = UIBadge.ContentType.Tag;

    /**
     * Only relevant for Pin layout.
     * Calculates if the badge should have fixed width.
     * Used to style the badge as a circle when it has a short text/number or an icon.
     *
     * Fixed width is applied when:
     * - there is only one text node with max 1 character
     * - there are multiple text nodes but all of them have max 1 character
     * - there are multiple text nodes but only one of them is an element node and all text nodes have max 1 character
     * @private
     * @returns {boolean}
     */
    private hasFixedWidth(): boolean {
        if (this.layout !== UIBadge.Layout.Pin) {
            return false;
        }

        let hasElementNode = false;
        let hasLongTextNode = false;
        let hasShortTextNode = false;

        Array.from(this.childNodes).forEach(
            (node: ChildNode): void | undefined => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    hasElementNode = true;
                    return;
                }

                if (node.nodeType === Node.TEXT_NODE) {
                    const nodeTextValue = node.textContent?.trim();

                    if (nodeTextValue?.length === 0) {
                        return;
                    }

                    if (
                        nodeTextValue?.length !== undefined &&
                        nodeTextValue.length > 1
                    ) {
                        hasLongTextNode = true;
                    } else {
                        hasShortTextNode = true;
                    }
                }
            }
        );
        return !hasLongTextNode && (!hasElementNode || !hasShortTextNode);
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        if (this.hasFixedWidth()) {
            this.classList.add('ui-badge__fixed-width');
        }
    }
}
